import { useState, useEffect, useCallback } from "react";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate } from "react-router-dom";
import { DownOutlined, Dropdown } from "@/config/antd";
import Breadcrumb from "@/components/specific/Breadcrumb";
import PageHeader from "@/components/layout/Header/Page_Header";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import CanvassCard from "@/pages/canvass/components/Canvass_Card";
import EmptyState from "@/pages/canvass/components/Empty_State";
import CanvassTable from "@/pages/canvass/components/CanvassTable";
import Success from "@/components/common/alerts/Success";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCanvass,
  selectAllCanvass,
  selectCanvassLoading,
  selectCanvassTotal,
} from "@/store/redux/slices/canvass/canvass_slice";

const Canvass = () => {
  const [searchValue, setSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [filterValue, setFilterValue] = useState("all"); // date filter: 'all' | 'today' | 'thisWeek' | 'thisMonth'
  const [currentPage, setCurrentPage] = useState(1);
  const [, setSelectedHouseholds] = useState([]);
  const pageSize = 10;
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canvassData = useSelector(selectAllCanvass);
  const totalCanvass = useSelector(selectCanvassTotal);
  const isLoading = useSelector(selectCanvassLoading);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [deletedHouseholdName] = useState("");

  // Fetch canvass data
  const fetchCanvassData = useCallback(async () => {
    const skip = (currentPage - 1) * pageSize;
    const limit = pageSize;

    showLoader();
    try {
      await dispatch(fetchCanvass({ skip, limit })).unwrap();
    } catch (error) {
      console.error("Error fetching canvass data:", error);
    } finally {
      hideLoader();
    }
  }, [currentPage, pageSize, dispatch, showLoader, hideLoader]);

  useEffect(() => {
    fetchCanvassData();
  }, [fetchCanvassData]);

  const applyDateFilter = useCallback(
    (list) => {
      if (!filterValue || filterValue === "all") return list;

      const now = new Date();

      return list.filter((item) => {
        const createdAt = item.createdAt ? new Date(item.createdAt) : null;
        if (!createdAt) return true; // keep records without a date

        const sameDay =
          createdAt.getDate() === now.getDate() &&
          createdAt.getMonth() === now.getMonth() &&
          createdAt.getFullYear() === now.getFullYear();

        if (filterValue === "today") {
          return sameDay;
        }

        if (filterValue === "thisWeek") {
          const startOfWeek = new Date(now);
          startOfWeek.setHours(0, 0, 0, 0);
          startOfWeek.setDate(now.getDate() - now.getDay());

          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 7);

          return createdAt >= startOfWeek && createdAt < endOfWeek;
        }

        if (filterValue === "thisMonth") {
          return (
            createdAt.getMonth() === now.getMonth() &&
            createdAt.getFullYear() === now.getFullYear()
          );
        }

        return true;
      });
    },
    [filterValue]
  );

  // Transform API data for table view
  const getFilteredTableData = useCallback(() => {
    if (!canvassData || canvassData.length === 0) {
      return [];
    }

    let filtered = canvassData.map((item) => ({
      key: item._id,
      _id: item._id,
      householdName: item.householdName || "No Name Provided",
      address: item.contactInformation?.address || "No Address Provided",
      members: item.numberOfHouseholdMembers || 0,
      canvasser: item.fullName || "No Name Provided", // You might want to add this to your API
      supportLevel:
        item.partySupport === "Strongly Support"
          ? 100
          : item.partySupport === "Somewhat Support"
          ? 75
          : item.partySupport === "Neutral"
          ? 50
          : item.partySupport === "Somewhat Oppose"
          ? 25
          : 0,
      createdAt: item.createdAt || null,
      dateTime: item.createdAt
        ? new Date(item.createdAt).toLocaleString()
        : "No Date",
      status: item.followUpNeeded ? "Pending" : "Complete",
    }));

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter((household) =>
        household.householdName
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "approved":
        filtered = filtered.filter(
          (household) => household.status === "Complete"
        );
        break;
      case "rejected":
        filtered = filtered.filter(
          (household) => household.status === "Rejected"
        );
        break;
      case "pending":
        filtered = filtered.filter(
          (household) => household.status === "Pending"
        );
        break;
      default:
        break;
    }

    // Apply date filter (Today / This Week / This Month)
    filtered = applyDateFilter(filtered);

    return filtered;
  }, [canvassData, searchValue, activeFilter, applyDateFilter]);

  // Get data for cards view (mobile)
  const getFilteredCardsData = useCallback(() => {
    return getFilteredTableData();
  }, [getFilteredTableData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleRefresh = useCallback(() => {
    // 1. Reset pagination to first page when refreshing
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all' to show all data
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("all");

    // 5. Fetch fresh data
    fetchCanvassData();
  }, [fetchCanvassData]);

  // Get counts for filters
  const getApprovedCount = useCallback(() => {
    return canvassData.filter((item) => !item.followUpNeeded).length;
  }, [canvassData]);

  const getRejectedCount = useCallback(() => {
    return canvassData.filter((item) => item.status === "Rejected").length;
  }, [canvassData]);

  const getPendingCount = useCallback(() => {
    return canvassData.filter((item) => item.followUpNeeded).length;
  }, [canvassData]);

  const handleAddTransaction = () => {
    navigate("/dashboard/canvass/add");
  };

  const handleSuccessClose = async () => {
    setSuccessModalVisible(false);

    // Refresh data after closing the success modal
    showLoader();
    try {
      // In a real app, this would fetch fresh data
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate API call
    } catch (error) {
      console.error("Error refreshing households after delete:", error);
    } finally {
      hideLoader();
    }
  };

  const handleModify = (id) => {
    showLoader();
    navigate(`/dashboard/canvass/edit/${id}`);
  };

  const handleFilterChange = (infoOrKey) => {
    const key =
      typeof infoOrKey === "string" ? infoOrKey : infoOrKey?.key ?? "today";
    setFilterValue(key);
  };

  const filterOptions = {
    items: [
      { key: "all", label: "All Time" },
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Households" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "pending", label: "Pending" },
  ];

  // breadcrumb items
  const breadCrumbItems = [{ title: "Canvass Management", link: "" }];

  return (
    <div className="w-full py-6">
      <Breadcrumb items={breadCrumbItems} />
      {/* Page Header */}
      <PageHeader title="Canvass Management" />

      <div className="flex items-center justify-between my-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1
              className="heading-medium text-lighterGrey"
              data-testid="canvass-count">
              {totalCanvass} Total Households
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of surveys made
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Households (${canvassData.length})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Approved (${getApprovedCount()})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "approved" ? "primary" : "white"}
              customTextColor={
                activeFilter === "approved" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("approved")}
            />
            <Button
              text={`Rejected (${getRejectedCount()})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "rejected" ? "primary" : "white"}
              customTextColor={
                activeFilter === "rejected" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("rejected")}
            />
            <Button
              text={`Pending (${getPendingCount()})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "pending" ? "primary" : "white"}
              customTextColor={activeFilter === "pending" ? "white" : "primary"}
              onClick={() => setActiveFilter("pending")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-3 lg:flex">
          <Dropdown menu={filterOptions} trigger={["click"]}>
            <Button
              text={
                filterOptions.items.find((opt) => opt.key === filterValue)
                  ?.label || "FILTER BY"
              }
              icon={<DownOutlined />}
              iconPosition="right"
              className="border border-primary font-normal text-regular !px-3 !py-2 h-8"
              customBgColor="white"
              customTextColor="black"
            />
          </Dropdown>
        </div>
        {/* <Button
          text="ADD NEW TRANSACTION"
          icon={<PlusOutlined />}
          color="primary"
          className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] sm:min-w-[100px] lg:hidden"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleAddTransaction}
        /> */}
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search..."
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      {/* Conditional Rendering based on data and filters */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          {/* Loading handled by global loader */}
        </div>
      ) : canvassData.length === 0 ? (
        <div data-testid="canvass-empty-state">
          <EmptyState onSetup={handleAddTransaction} />
        </div>
      ) : getFilteredTableData().length === 0 ? (
        <div data-testid="canvass-empty-state">
          <EmptyState onSetup={handleAddTransaction} />
        </div>
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden lg:block">
            <CanvassTable
              data={getFilteredTableData()}
              setSelectedHouseholds={setSelectedHouseholds}
              // onDelete={handleDelete}
              onTableChange={handleRefresh}
            />
          </div>

          {/* Mobile card view */}
          <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:hidden">
            {getFilteredCardsData().map((household) => (
              <CanvassCard
                key={household._id}
                id={household._id}
                householdName={household.householdName}
                address={household.address}
                members={household.members}
                canvasser={household.canvasser}
                status={household.status}
                // onDelete={handleDelete}
                onModify={handleModify}
              />
            ))}
          </div>

          <div className="mt-6">
            <CustomPagination
              current={currentPage}
              total={totalCanvass}
              pageSize={pageSize}
              onChange={handlePageChange}
              className="pb-8"
            />
          </div>
        </>
      )}

      {/* Success Modal */}
      <Success
        isVisible={successModalVisible}
        onClose={handleSuccessClose}
        header="Household Deleted"
        paragraph={`"${deletedHouseholdName}" has been successfully deleted.`}
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleSuccessClose}
      />
    </div>
  );
};

export default Canvass;
